import supabase from "./supabaseClient";

/**
 * ============================================================
 * SHIVEDA IT SERVICES — PROJECTS & MEDIA SERVICE
 * ============================================================
 * Centralized service layer interfacing directly with Supabase
 * PostgreSQL database and Supabase Storage bucket (`project-media`).
 *
 * Implements Row Level Security (RLS) enforcement, normalization,
 * and resilient fallbacks.
 * ============================================================
 */

/**
 * Normalizes a Supabase PostgreSQL row into the schema expected by the UI.
 * Provides backward-compatible property access (`Services`, `problem`, `stack`, `category`).
 */
export const mapDatabaseProjectToUI = (row) => {
  if (!row) return null;

  const stack = Array.isArray(row.project_technologies)
    ? row.project_technologies
        .map((pt) => pt.technologies?.name || pt.technologies?.slug)
        .filter(Boolean)
    : [];

  const media = Array.isArray(row.project_media)
    ? row.project_media.map((m) => ({
        ...m,
        publicUrl: supabase.storage.from("project-media").getPublicUrl(m.storage_path).data.publicUrl,
      }))
    : [];

  const thumbnail = media.find((m) => m.media_type === "thumbnail")?.publicUrl || null;
  const heroImage = media.find((m) => m.media_type === "hero")?.publicUrl || null;

  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    client: row.client_name || "",
    client_name: row.client_name || "",
    category: row.categories?.name || "Other",
    category_id: row.category_id,
    status: row.project_status || "Completed",
    project_status: row.project_status || "Completed",
    problem: row.challenge_description || "",
    Services: row.solution_description || "", // Backward-compatible with mock.js
    solution_description: row.solution_description || "",
    challenge_description: row.challenge_description || "",
    outcome: row.outcome_description || "",
    outcome_description: row.outcome_description || "",
    short_summary: row.short_summary || "",
    stack,
    project_technologies: row.project_technologies || [],
    url: row.live_url || "",
    live_url: row.live_url || "",
    github_url: row.github_url || "",
    metrics: row.metrics || {},
    is_published: Boolean(row.is_published),
    is_featured: Boolean(row.is_featured),
    display_order: Number(row.display_order ?? 0),
    meta_title: row.meta_title || "",
    meta_description: row.meta_description || "",
    testimonial_quote: row.testimonial_quote || null,
    testimonial_author: row.testimonial_author || null,
    testimonial_role: row.testimonial_role || null,
    testimonial_rating: row.testimonial_rating || 5,
    team_size: row.team_size || null,
    deleted_at: row.deleted_at || null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    media,
    thumbnail,
    heroImage,
  };
};

export const projectsService = {
  // --------------------------------------------------------------------------
  // 1. Public Site Data Operations
  // --------------------------------------------------------------------------

  /**
   * Fetches all published, non-deleted projects ordered by display_order ascending.
   * Falls back cleanly to local mock data if the network is unreachable.
   */
  async getPublishedProjects() {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          *,
          categories (id, name, slug, description),
          project_technologies (
            technologies (id, name, slug)
          ),
          project_media (*)
        `
        )
        .eq("is_published", true)
        .is("deleted_at", null)
        .order("display_order", { ascending: true });

      if (error) {
        console.error("[projectsService] Supabase error in getPublishedProjects:", error.message);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      return data.map(mapDatabaseProjectToUI);
    } catch (err) {
      console.error("[projectsService] Exception during project fetch:", err);
      return [];
    }
  },

  /**
   * Fetches a single published project by its URL slug.
   */
  async getProjectBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          *,
          categories (*),
          project_technologies (
            technologies (*)
          ),
          project_media (*)
        `
        )
        .eq("slug", slug)
        .eq("is_published", true)
        .is("deleted_at", null)
        .maybeSingle();

      if (error || !data) {
        return null;
      }

      return mapDatabaseProjectToUI(data);
    } catch (err) {
      console.error(`[projectsService] Exception fetching slug '${slug}':`, err);
      return null;
    }
  },

  // --------------------------------------------------------------------------
  // 2. Admin Project Management Operations
  // --------------------------------------------------------------------------

  /**
   * Fetches projects for admin listing with optional filtering and search.
   */
  async getAllProjectsAdmin({
    includeDeleted = false,
    category = "All",
    status = "All",
    published = "All",
    search = "",
  } = {}) {
    let query = supabase
      .from("projects")
      .select(
        `
        *,
        categories (id, name, slug),
        project_technologies (
          technologies (id, name, slug)
        ),
        project_media (*)
      `
      )
      .order("display_order", { ascending: true });

    // Filter by deletion / trash state
    if (published === "trash") {
      query = query.not("deleted_at", "is", null);
    } else if (!includeDeleted) {
      query = query.is("deleted_at", null);
    }

    // Filter by publication state
    if (published === "published") {
      query = query.eq("is_published", true);
    } else if (published === "draft") {
      query = query.eq("is_published", false);
    }

    // Filter by project status
    if (status && status !== "All") {
      query = query.eq("project_status", status);
    }

    // Filter by category
    if (category && category !== "All") {
      query = query.eq("category_id", category);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch projects: ${error.message}`);
    }

    let results = (data || []).map(mapDatabaseProjectToUI);

    // Apply client-side text search if provided
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      results = results.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q) ||
          p.client.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.stack.some((s) => s.toLowerCase().includes(q))
      );
    }

    return results;
  },

  /**
   * Fetches a project by its primary UUID for the admin editor.
   */
  async getProjectById(id) {
    const { data, error } = await supabase
      .from("projects")
      .select(
        `
        *,
        categories (*),
        project_technologies (
          technologies (*)
        ),
        project_media (*)
      `
      )
      .eq("id", id)
      .single();

    if (error) {
      throw new Error(`Failed to load project: ${error.message}`);
    }

    return mapDatabaseProjectToUI(data);
  },

  /**
   * Fetches a project by slug or UUID for the admin editor (clean human-readable routes).
   */
  async getProjectBySlugOrId(slugOrId) {
    if (!slugOrId) return null;
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slugOrId);
    if (isUuid) {
      return this.getProjectById(slugOrId);
    }

    const { data, error } = await supabase
      .from("projects")
      .select(
        `
        *,
        categories (*),
        project_technologies (
          technologies (*)
        ),
        project_media (*)
      `
      )
      .eq("slug", slugOrId)
      .single();

    if (error) {
      throw new Error(`Failed to load project: ${error.message}`);
    }

    return mapDatabaseProjectToUI(data);
  },

  /**
   * Creates a new project in Supabase with technology associations.
   */
  async createProject(projectData, technologyIds = []) {
    // 1. Determine next display_order if not explicitly set
    let displayOrder = Number(projectData.display_order);
    if (isNaN(displayOrder) || displayOrder <= 0) {
      const { data: maxOrderData } = await supabase
        .from("projects")
        .select("display_order")
        .order("display_order", { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData?.[0]?.display_order || 0;
      displayOrder = maxOrder + 1;
    }

    const payload = {
      title: projectData.title.trim(),
      slug: projectData.slug.trim().toLowerCase(),
      client_name: projectData.client_name?.trim() || null,
      category_id: projectData.category_id,
      project_status: projectData.project_status || "Ongoing",
      short_summary: projectData.short_summary?.trim() || null,
      challenge_description: projectData.challenge_description?.trim() || null,
      solution_description: projectData.solution_description?.trim() || null,
      outcome_description: projectData.outcome_description?.trim() || null,
      metrics: projectData.metrics || {},
      live_url: projectData.live_url?.trim() || null,
      github_url: projectData.github_url?.trim() || null,
      is_published: Boolean(projectData.is_published),
      is_featured: Boolean(projectData.is_featured),
      display_order: displayOrder,
      meta_title: projectData.meta_title?.trim() || null,
      meta_description: projectData.meta_description?.trim() || null,
      testimonial_quote: projectData.testimonial_quote?.trim() || null,
      testimonial_author: projectData.testimonial_author?.trim() || null,
      testimonial_role: projectData.testimonial_role?.trim() || null,
      testimonial_rating: Number(projectData.testimonial_rating) || 5,
      team_size: projectData.team_size?.trim() || null,
    };

    const { data: newProject, error: projectError } = await supabase
      .from("projects")
      .insert(payload)
      .select()
      .single();

    if (projectError) {
      throw new Error(`Could not create project: ${projectError.message}`);
    }

    // 2. Link technologies if provided
    if (Array.isArray(technologyIds) && technologyIds.length > 0) {
      const links = technologyIds.map((techId) => ({
        project_id: newProject.id,
        technology_id: techId,
      }));

      const { error: techError } = await supabase
        .from("project_technologies")
        .insert(links);

      if (techError) {
        console.warn("[projectsService] Warning creating tech associations:", techError);
      }
    }

    return this.getProjectById(newProject.id);
  },

  /**
   * Updates an existing project and synchronizes technology associations.
   */
  async updateProject(id, projectData, technologyIds = null) {
    const payload = {
      title: projectData.title.trim(),
      slug: projectData.slug.trim().toLowerCase(),
      client_name: projectData.client_name?.trim() || null,
      category_id: projectData.category_id,
      project_status: projectData.project_status || "Ongoing",
      short_summary: projectData.short_summary?.trim() || null,
      challenge_description: projectData.challenge_description?.trim() || null,
      solution_description: projectData.solution_description?.trim() || null,
      outcome_description: projectData.outcome_description?.trim() || null,
      metrics: projectData.metrics || {},
      live_url: projectData.live_url?.trim() || null,
      github_url: projectData.github_url?.trim() || null,
      is_published: Boolean(projectData.is_published),
      is_featured: Boolean(projectData.is_featured),
      display_order: Number(projectData.display_order ?? 0),
      meta_title: projectData.meta_title?.trim() || null,
      meta_description: projectData.meta_description?.trim() || null,
      testimonial_quote: projectData.testimonial_quote?.trim() || null,
      testimonial_author: projectData.testimonial_author?.trim() || null,
      testimonial_role: projectData.testimonial_role?.trim() || null,
      testimonial_rating: Number(projectData.testimonial_rating) || 5,
      team_size: projectData.team_size?.trim() || null,
    };

    const { error: updateError } = await supabase
      .from("projects")
      .update(payload)
      .eq("id", id);

    if (updateError) {
      throw new Error(`Could not update project: ${updateError.message}`);
    }

    // Synchronize technology associations if passed
    if (Array.isArray(technologyIds)) {
      // Remove previous associations
      await supabase.from("project_technologies").delete().eq("project_id", id);

      if (technologyIds.length > 0) {
        const links = technologyIds.map((techId) => ({
          project_id: id,
          technology_id: techId,
        }));
        await supabase.from("project_technologies").insert(links);
      }
    }

    return this.getProjectById(id);
  },

  /**
   * Quick toggle of publication state.
   */
  async togglePublish(id, isPublished) {
    const { error } = await supabase
      .from("projects")
      .update({ is_published: Boolean(isPublished) })
      .eq("id", id);

    if (error) {
      throw new Error(`Failed to update publish status: ${error.message}`);
    }
    return true;
  },

  /**
   * Quick toggle of featured state.
   */
  async toggleFeatured(id, isFeatured) {
    const { error } = await supabase
      .from("projects")
      .update({ is_featured: Boolean(isFeatured) })
      .eq("id", id);

    if (error) {
      throw new Error(`Failed to update featured status: ${error.message}`);
    }
    return true;
  },

  /**
   * Soft deletes a project by populating `deleted_at`.
   */
  async softDeleteProject(id) {
    const { error } = await supabase
      .from("projects")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      throw new Error(`Failed to soft-delete project: ${error.message}`);
    }
    return true;
  },

  /**
   * Restores a soft-deleted project by clearing `deleted_at`.
   */
  async restoreProject(id) {
    const { error } = await supabase
      .from("projects")
      .update({ deleted_at: null })
      .eq("id", id);

    if (error) {
      throw new Error(`Failed to restore project: ${error.message}`);
    }
    return true;
  },

  /**
   * Permanently deletes a project and any associated storage media.
   */
  async hardDeleteProject(id) {
    // 1. Find and delete media files from Supabase Storage
    const { data: mediaItems } = await supabase
      .from("project_media")
      .select("storage_path")
      .eq("project_id", id);

    if (mediaItems && mediaItems.length > 0) {
      const paths = mediaItems.map((m) => m.storage_path).filter(Boolean);
      if (paths.length > 0) {
        await supabase.storage.from("project-media").remove(paths);
      }
    }

    // 2. Delete project row (cascade handles associations)
    const { error } = await supabase.from("projects").delete().eq("id", id);

    if (error) {
      throw new Error(`Failed to permanently delete project: ${error.message}`);
    }
    return true;
  },

  /**
   * Batch updates display_order for a list of projects.
   */
  async reorderProjects(orderedItems) {
    const updates = orderedItems.map((item, index) =>
      supabase
        .from("projects")
        .update({ display_order: index + 1 })
        .eq("id", item.id)
    );

    const results = await Promise.all(updates);
    const failed = results.find((r) => r.error);
    if (failed?.error) {
      throw new Error(`Failed to reorder projects: ${failed.error.message}`);
    }
    return true;
  },

  // --------------------------------------------------------------------------
  // 3. Taxonomy: Categories & Technologies
  // --------------------------------------------------------------------------

  async getCategories() {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name, slug, description")
        .order("name", { ascending: true });

      if (error || !data) {
        return [];
      }

      return data;
    } catch (err) {
      console.error("[projectsService] Failed to fetch categories:", err);
      return [];
    }
  },

  async createCategory({ name, slug, description }) {
    const cleanSlug = (slug || name)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

    const { data, error } = await supabase
      .from("categories")
      .insert({
        name: name.trim(),
        slug: cleanSlug,
        description: description?.trim() || null,
      })
      .select()
      .single();

    if (error) throw new Error(`Could not create category: ${error.message}`);
    return data;
  },

  async updateCategory(id, { name, slug, description }) {
    const cleanSlug = (slug || name)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

    const { data, error } = await supabase
      .from("categories")
      .update({
        name: name.trim(),
        slug: cleanSlug,
        description: description?.trim() || null,
      })
      .eq("id", id)
      .select()
      .single();

    if (error) throw new Error(`Could not update category: ${error.message}`);
    return data;
  },

  async deleteCategory(id) {
    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (error) throw new Error(`Could not delete category: ${error.message}`);
    return true;
  },

  async getTechnologies() {
    try {
      const { data, error } = await supabase
        .from("technologies")
        .select("id, name, slug")
        .order("name", { ascending: true });

      if (error) throw new Error(error.message);
      return data || [];
    } catch (err) {
      console.warn("[projectsService] Failed to fetch technologies:", err);
      return [];
    }
  },

  async createTechnology({ name, slug }) {
    const cleanSlug = (slug || name)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

    const { data, error } = await supabase
      .from("technologies")
      .insert({
        name: name.trim(),
        slug: cleanSlug,
      })
      .select()
      .single();

    if (error) throw new Error(`Could not create technology: ${error.message}`);
    return data;
  },

  async deleteTechnology(id) {
    const { error } = await supabase.from("technologies").delete().eq("id", id);
    if (error) throw new Error(`Could not delete technology: ${error.message}`);
    return true;
  },

  // --------------------------------------------------------------------------
  // 4. Supabase Storage & Media Operations (Phase 5)
  // --------------------------------------------------------------------------

  /**
   * Uploads an asset directly to Supabase Storage bucket `project-media`
   * and creates a corresponding record in `public.project_media`.
   */
  async uploadProjectMedia(file, projectId, { mediaType = "gallery", altText = "", caption = "", displayOrder = 0 } = {}) {
    if (!file) throw new Error("No file provided for upload.");
    if (!projectId) throw new Error("Project ID is required to link media.");

    // Validate size (maximum 10MB)
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      throw new Error("File exceeds 10MB limit. Please upload an optimized asset.");
    }

    // Build sanitized storage path
    const sanitizedName = file.name.toLowerCase().replace(/[^a-z0-9._-]/g, "_");
    const storagePath = `projects/${projectId}/${Date.now()}_${sanitizedName}`;

    // Upload object to private Supabase Storage bucket `project-media`
    const { error: uploadError } = await supabase.storage
      .from("project-media")
      .upload(storagePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Supabase Storage upload failed: ${uploadError.message}`);
    }

    // Insert reference in project_media table
    const { data: mediaRecord, error: dbError } = await supabase
      .from("project_media")
      .insert({
        project_id: projectId,
        storage_path: storagePath,
        media_type: mediaType,
        alt_text: altText?.trim() || file.name,
        caption: caption?.trim() || null,
        display_order: Number(displayOrder || 0),
      })
      .select()
      .single();

    if (dbError) {
      // Clean up orphaned storage object if database insert fails
      await supabase.storage.from("project-media").remove([storagePath]);
      throw new Error(`Failed to record media in database: ${dbError.message}`);
    }

    const { data: urlData } = supabase.storage.from("project-media").getPublicUrl(storagePath);

    return {
      ...mediaRecord,
      publicUrl: urlData.publicUrl,
    };
  },

  /**
   * Deletes a media asset from Supabase Storage and removes the database row.
   */
  async deleteProjectMedia(mediaId, storagePath) {
    if (storagePath) {
      await supabase.storage.from("project-media").remove([storagePath]);
    }

    const { error } = await supabase
      .from("project_media")
      .delete()
      .eq("id", mediaId);

    if (error) {
      throw new Error(`Failed to delete media record: ${error.message}`);
    }

    return true;
  },

  /**
   * Fetches all media records for a given project.
   */
  async getProjectMedia(projectId) {
    const { data, error } = await supabase
      .from("project_media")
      .select("*")
      .eq("project_id", projectId)
      .order("display_order", { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch project media: ${error.message}`);
    }

    return (data || []).map((m) => ({
      ...m,
      publicUrl: supabase.storage.from("project-media").getPublicUrl(m.storage_path).data.publicUrl,
    }));
  },
};

// Legacy backward-compatible exports for existing imports
export const getPublishedProjects = projectsService.getPublishedProjects;
export const getProjectBySlug = projectsService.getProjectBySlug;
export const getCategories = projectsService.getCategories;

export default projectsService;
